<template>
    <div class="pa-5">
        <v-row>
            <v-col cols="12">
                <v-card>
                    <v-data-table
                        :headers="headers"
                        :items="value"
                        :mobile-breakpoint="0"
                        :items-per-page="-1"
                        item-key="uniqueKey"
                        hide-default-footer
                    >
                        <template #item.paperId="{item}">
                            <div class="pa-2">
                                <v-select
                                    v-model="item.paperId"
                                    :items="getAvailablePapersForItem(item)"
                                    item-value="id"
                                    item-text="name"
                                    dense
                                    outlined
                                    :error-messages="getPaperDuplicateError(item)"
                                    :error="hasPaperDuplicate(item)"
                                    :hide-details="!hasItemValidationError(item)"
                                />
                            </div>
                        </template>
                        <template #item.makeReadyDuration="{item}">
                            <div class="pa-2">
                                <v-text-field
                                    v-model.number="item.makeReadyDuration"
                                    type="number"
                                    min="1"
                                    max="999999"
                                    dense
                                    outlined
                                    :error-messages="getMakeReadyDurationError(item)"
                                    :error="hasMakeReadyDurationError(item)"
                                    :hide-details="!hasItemValidationError(item)"
                                />
                            </div>
                        </template>
                        <template #item.metersPerMinute="{item}">
                            <div class="pa-2">
                                <v-text-field
                                    v-model.number="item.metersPerMinute"
                                    type="number"
                                    min="1"
                                    max="999999"
                                    dense
                                    outlined
                                    :error-messages="getMetersPerMinuteError(item)"
                                    :error="hasMetersPerMinuteError(item)"
                                    :hide-details="!hasItemValidationError(item)"
                                />
                            </div>
                        </template>
                        <template #item.actions="{item}">
                            <v-btn
                                icon
                                color="error"
                                data-sentry-label="Usuń konfigurację krojenia z roli"
                                @click="removeRow(item)"
                            >
                                <v-icon>mdi-delete</v-icon>
                            </v-btn>
                        </template>
                        <template #no-data>
                            <div class="text-center py-5 grey--text">
                                Brak danych. Dodaj konfigurację klikając przycisk poniżej.
                            </div>
                        </template>
                        <template #footer>
                            <div class="pa-3">
                                <v-btn
                                    color="secondary"
                                    small
                                    data-sentry-label="Dodaj kolejną konfigurację wydajności krojenia z roli"
                                    @click="addNewRow"
                                >
                                    <v-icon left small>
                                        mdi-plus
                                    </v-icon>
                                    Dodaj kolejną konfigurację
                                </v-btn>
                            </div>
                        </template>
                    </v-data-table>
                </v-card>
            </v-col>
        </v-row>
    </div>
</template>

<script>
export default {
    name: 'CuttingFromRollTab',
    props: {
        value: {
            type: Array,
            default: () => []
        },
        papers: {
            type: Array,
            default: () => []
        },
        validation: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            uniqueKeyCounter: 0,
            headers: [
                {
                    text: 'Surowiec',
                    value: 'paperId',
                    sortable: false,
                    align: 'center',
                    width: '36%'
                },
                {
                    text: 'Czas przyrządu (min.)',
                    value: 'makeReadyDuration',
                    sortable: false,
                    align: 'center',
                    width: '27%'
                },
                {
                    text: 'Prędkość pracy (m/min.)',
                    value: 'metersPerMinute',
                    sortable: false,
                    align: 'center',
                    width: '27%'
                },
                {
                    text: 'Usuń',
                    value: 'actions',
                    sortable: false,
                    align: 'center'
                }
            ]
        }
    },
    methods: {
        addNewRow() {
            this.$emit('input', [
                ...this.value,
                {
                    paperId: null,
                    makeReadyDuration: 0,
                    metersPerMinute: 0,
                    uniqueKey: `new-cutting-from-roll-${this.uniqueKeyCounter++}`
                }
            ])
        },
        removeRow(item) {
            this.$emit(
                'input',
                this.value.filter(config => config.uniqueKey !== item.uniqueKey)
            )
        },
        hasItemValidationError(item) {
            return (
                this.hasPaperDuplicate(item) ||
                this.hasMakeReadyDurationError(item) ||
                this.hasMetersPerMinuteError(item)
            )
        },
        hasPaperDuplicate(item) {
            return this.value.some(
                config => config.uniqueKey !== item.uniqueKey && config.paperId === item.paperId
            )
        },
        getPaperDuplicateError(item) {
            if (this.hasPaperDuplicate(item)) {
                return ['Ten surowiec jest już wybrany w innej konfiguracji']
            }

            return []
        },
        getMakeReadyDurationError(item) {
            if (!this.validation || !this.validation.$each) return []

            const index = this.value.indexOf(item)

            const rowVal = this.validation.$each[index]

            if (!rowVal || !rowVal.makeReadyDuration || !rowVal.makeReadyDuration.$invalid) return []

            const v = rowVal.makeReadyDuration

            const err = []

            if (!v.required) err.push('Czas przyrządu jest wymagany')
            else if (!v.integer) err.push('Wymagana liczba całkowita')
            else if (!v.minValue) err.push('Wartość musi być większa od 0')

            return err
        },
        hasMakeReadyDurationError(item) {
            return this.getMakeReadyDurationError(item).length > 0
        },
        getMetersPerMinuteError(item) {
            if (!this.validation || !this.validation.$each) return []

            const index = this.value.indexOf(item)

            const rowVal = this.validation.$each[index]

            if (!rowVal || !rowVal.metersPerMinute || !rowVal.metersPerMinute.$invalid) return []

            const v = rowVal.metersPerMinute

            const err = []

            if (!v.required) err.push('Prędkość (m/min.) jest wymagana')
            else if (!v.integer) err.push('Wymagana liczba całkowita')
            else if (!v.minValue) err.push('Wartość musi być większa od 0')

            return err
        },
        hasMetersPerMinuteError(item) {
            return this.getMetersPerMinuteError(item).length > 0
        },
        getAvailablePapersForItem(item) {
            const noneOption = { id: null, name: 'brak' }
            const usedByOtherRows = this.value
                .filter(config => config.uniqueKey !== item.uniqueKey && config.paperId != null)
                .map(config => config.paperId)

            const available = this.papers.filter(paper => !usedByOtherRows.includes(paper.id))

            return [noneOption, ...available]
        }
    }
}
</script>
